import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import {
  ShieldCheck, AlertTriangle, Users, MessageCircle, Trash2,
  CheckCircle, Ban, RotateCcw, Flag
} from "lucide-react";

const TABS = [
  { id: "reports", label: "Reports", icon: Flag },
  { id: "users", label: "Users", icon: Users },
  { id: "threads", label: "Threads", icon: MessageCircle },
];

export default function Admin() {
  const [tab, setTab] = useState("reports");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch {}
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14" data-testid="admin-page">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#D96C4A]">
          <ShieldCheck size={14} /> Moderation
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#2D3B31] mt-3">
          Guardian of the sabhā.
        </h1>
        <p className="mt-3 text-[#5C6B5F] max-w-2xl">
          Review flagged content, oversee members, and preserve the harmony of this gathering.
        </p>
      </div>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10" data-testid="admin-stats">
          <StatBox label="Members" value={stats.users} />
          <StatBox label="Threads" value={stats.threads} />
          <StatBox label="Replies" value={stats.replies} />
          <StatBox label="Pending reports" value={stats.pending_reports} highlight={stats.pending_reports > 0} />
          <StatBox label="Suspended" value={stats.banned} />
        </div>
      )}

      <div className="mt-10 flex gap-2 border-b" style={{ borderColor: "#EAE5D9" }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#D96C4A] text-[#D96C4A]"
                  : "border-transparent text-[#5C6B5F] hover:text-[#2D3B31]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <Icon size={16} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "reports" && <ReportsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "threads" && <ThreadsTab />}
      </div>
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div
      className="organic-card p-5"
      style={highlight ? { background: "#F2D8CF", borderColor: "#D96C4A" } : {}}
    >
      <div className="text-xs uppercase tracking-widest text-[#8A948C]">{label}</div>
      <div className="font-serif-display text-3xl text-[#2D3B31] mt-1">{value}</div>
    </div>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const removeTarget = async (r) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      if (r.target_type === "thread") {
        await api.delete(`/threads/${r.target_id}`);
      } else {
        await api.delete(`/replies/${r.target_id}`);
      }
      await api.post(`/admin/reports/${r.id}/resolve`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const resolve = async (r) => {
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (reports === null) return <Loading />;
  if (reports.length === 0)
    return <EmptyMsg icon={<AlertTriangle size={24} color="#8A948C" />} title="No reports" text="All quiet. The sabhā rests in peace." />;

  return (
    <div className="space-y-4" data-testid="admin-reports-list">
      {error && <div className="text-sm text-[#C05B3B]">{error}</div>}
      {reports.map((r) => (
        <div key={r.id} className="organic-card p-6" data-testid={`report-item-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="tag">{r.target_type}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    r.status === "pending"
                      ? "bg-[#F2D8CF] text-[#C05B3B]"
                      : "bg-[#F3EFE9] text-[#5C6B5F]"
                  }`}
                >
                  {r.status}
                </span>
                <span className="text-xs text-[#8A948C]">
                  Reported by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              <div className="mt-3 text-sm text-[#2D3B31] font-medium">
                Reason: <span className="font-normal text-[#5C6B5F]">{r.reason}</span>
              </div>
              {r.target_preview ? (
                <div className="mt-4 p-4 rounded-xl bg-[#F3EFE9] text-sm text-[#2D3B31]">
                  {r.target_preview.title && <div className="font-serif-display text-lg">{r.target_preview.title}</div>}
                  <div className="text-[#5C6B5F] mt-1">{r.target_preview.body}</div>
                  {r.target_preview.deleted && (
                    <div className="mt-2 text-xs text-[#C05B3B]">(Already deleted)</div>
                  )}
                </div>
              ) : (
                <div className="mt-3 text-xs text-[#8A948C] italic">Target not found.</div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {r.status === "pending" && r.target_preview && !r.target_preview.deleted && (
                <button onClick={() => removeTarget(r)} className="btn-accent inline-flex items-center gap-1.5" data-testid={`report-delete-${r.id}`}>
                  <Trash2 size={14} /> Delete & resolve
                </button>
              )}
              {r.status === "pending" && (
                <button onClick={() => resolve(r)} className="btn-outline inline-flex items-center gap-1.5" data-testid={`report-resolve-${r.id}`}>
                  <CheckCircle size={14} /> Mark resolved
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleBan = async (u) => {
    if (!window.confirm(u.banned ? `Unsuspend ${u.name}?` : `Suspend ${u.name}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/ban`, { banned: !u.banned });
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (users === null) return <Loading />;

  return (
    <div className="organic-card overflow-hidden" data-testid="admin-users-list">
      {error && <div className="p-4 text-sm text-[#C05B3B]">{error}</div>}
      <table className="w-full">
        <thead className="bg-[#F3EFE9]">
          <tr className="text-left text-xs uppercase tracking-widest text-[#8A948C]">
            <th className="px-6 py-4">Member</th>
            <th className="px-6 py-4">Role</th>
            <th className="px-6 py-4">Activity</th>
            <th className="px-6 py-4">Status</th>
            <th className="px-6 py-4"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t" style={{ borderColor: "#EAE5D9" }} data-testid={`user-row-${u.id}`}>
              <td className="px-6 py-4">
                <div className="font-medium text-[#2D3B31]">{u.name}</div>
                <div className="text-xs text-[#8A948C]">{u.email}</div>
              </td>
              <td className="px-6 py-4 text-sm">
                {u.role === "admin" ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#C05B3B] bg-[#F2D8CF] px-2 py-0.5 rounded-full">
                    <ShieldCheck size={12} /> admin
                  </span>
                ) : (
                  <span className="text-[#5C6B5F]">seeker</span>
                )}
              </td>
              <td className="px-6 py-4 text-sm text-[#5C6B5F]">
                {u.thread_count} threads · {u.reply_count} replies
              </td>
              <td className="px-6 py-4 text-sm">
                {u.banned ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#C05B3B] bg-[#F2D8CF] px-2 py-0.5 rounded-full">
                    <Ban size={12} /> Suspended
                  </span>
                ) : (
                  <span className="text-xs text-[#5C6B5F]">Active</span>
                )}
              </td>
              <td className="px-6 py-4 text-right">
                {u.role !== "admin" && (
                  <button
                    onClick={() => toggleBan(u)}
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAE5D9] hover:border-[#D96C4A] hover:text-[#D96C4A]"
                    data-testid={`ban-toggle-${u.id}`}
                  >
                    {u.banned ? (
                      <>
                        <RotateCcw size={12} /> Unsuspend
                      </>
                    ) : (
                      <>
                        <Ban size={12} /> Suspend
                      </>
                    )}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ThreadsTab() {
  const [threads, setThreads] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/threads");
      setThreads(data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm("Delete this thread?")) return;
    try {
      await api.delete(`/threads/${t.id}`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (threads === null) return <Loading />;

  return (
    <div className="space-y-3" data-testid="admin-threads-list">
      {error && <div className="text-sm text-[#C05B3B]">{error}</div>}
      {threads.length === 0 ? (
        <EmptyMsg icon={<MessageCircle size={24} color="#8A948C" />} title="No threads yet" text="The conversation has not yet begun." />
      ) : (
        threads.map((t) => (
          <div key={t.id} className="organic-card p-5 flex items-start justify-between gap-4" data-testid={`admin-thread-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#8A948C]">
                Chapter {t.chapter_number} · by {t.author?.name} · {new Date(t.created_at).toLocaleString()}
                {t.deleted && <span className="ml-2 text-[#C05B3B]">[deleted]</span>}
              </div>
              <Link to={`/threads/${t.id}`} className="font-serif-display text-lg text-[#2D3B31] hover:text-[#D96C4A] block mt-1">
                {t.title}
              </Link>
              <p className="text-sm text-[#5C6B5F] mt-1 line-clamp-2">{t.body}</p>
            </div>
            {!t.deleted && (
              <button onClick={() => remove(t)} className="text-xs text-[#8A948C] hover:text-[#C05B3B] inline-flex items-center gap-1" data-testid={`admin-thread-delete-${t.id}`}>
                <Trash2 size={14} /> Remove
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center p-14">
      <div className="w-8 h-8 rounded-full border-2 border-[#EAE5D9] border-t-[#D96C4A] animate-spin" />
    </div>
  );
}

function EmptyMsg({ icon, title, text }) {
  return (
    <div className="organic-card p-10 text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      <div className="font-serif-display text-2xl text-[#2D3B31]">{title}</div>
      <p className="text-sm text-[#5C6B5F] mt-2">{text}</p>
    </div>
  );
}
